import clsx from 'clsx';
import { Form, Formik } from 'formik';
import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import Button from '@components/Button';
import SectionTitle from '@components/SectionTitle';
import { useSubscribeForm } from '@contexts/SubscribeFormContext';
import { useSubscriptionUpdateForCbpt } from '@hooks';
import { RECAPTCHA_SITE_KEY } from '@src/env';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';
import { SelectedExtendedOffer } from '@utils/backend-api/subscribe';
import { buildQueryString, extractQueryParams } from '@utils/queryStringHelper';

import { TextField } from '../../../FormFields';
import CustomSpinner from '../../../Spinner/CustomSpinner';
import { SubscriptionFormProps } from '../../../SubscriptionForm';
import Disclosure from '../../../SubscriptionForm/StripeDisclosure';

interface PaymentMethodProps
  extends Omit<SubscriptionFormProps, 'userEmail' | 'userFullName' | 'dob' | 'formLocation'> {
  isReferral?: boolean;
  selectedOffer: SelectedExtendedOffer;
  subscriptionId?: string;
}

interface FormValues {
  username: string;
}

const PaymentMethod = ({
  buttonText,
  isReferral,
  presentedOfferCodes,
  referringSubscriberName,
  referringSubscriberGuid,
  selectedOffer: { code, currentPrice, billingFrequency, isPro, originalPrice },
  subscriptionId,
}: PaymentMethodProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const reCaptchaRef = useRef<ReCAPTCHA>();
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: updateSubscriptionForCbpt } = useSubscriptionUpdateForCbpt();
  const protocol = window.location.protocol;
  const host = window.location.host;
  const { article_id, modal } = extractQueryParams(['article_id', 'modal']);
  const hasDiscount = currentPrice !== originalPrice;
  const {
    user: { nextBillingDate, isUserCBPT },
  } = useSubscribeForm();

  const handlePayment = async (): Promise<never | { error: StripeError }> => {
    const queryString = buildQueryString({
      article_id,
      modal,
      code,
      presented_offer_codes: presentedOfferCodes,
      guid: referringSubscriberGuid,
      subscription_id: subscriptionId,
    });

    const returnUrl = `${protocol}//${host}/payment-status?${queryString}`;
    const paymentResponse = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Can't use READER_URL for testing, so this is used to build URLs dynamically
        return_url: returnUrl,
      },
      redirect: 'always',
    });

    return { error: paymentResponse.error };
  };

  const handleSubmit = async ({ username }: FormValues) => {
    // Username is a random hidden field to prevent bots from submitting the form, human users will not see this but bots will
    // so if the field is filled out, we know it's a bot and we don't submit the form.
    if (username) {
      return;
    }

    if (isUserCBPT) {
      setIsLoading(true);
      updateSubscriptionForCbpt({
        page: window.location.pathname,
        presented_offer_codes: presentedOfferCodes,
        code,
        source: 'web',
      });
      setIsLoading(true);
      return;
    }

    if (!stripe || !elements) {
      setErrorMessage('Still loading Stripe. Please wait a few seconds and then try again.');
      return;
    }

    setIsLoading(true);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message);
      setIsLoading(false);
      return;
    }

    const { error } = await handlePayment();

    setIsLoading(false);
    if (error) {
      setErrorMessage(error.message);
      return;
    }
  };

  return (
    <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={reCaptchaRef}>
      <Formik initialValues={{ username: '' }} onSubmit={handleSubmit}>
        {() => (
          <Form>
            <SectionTitle title="Payment information" />
            <div className="m-auto max-w-[520px]">
              <TextField className="hidden" name="username" />
              {!isUserCBPT && (
                <div id="payment-form">
                  <PaymentElement
                    onChange={() => setErrorMessage('')}
                    options={{
                      layout: {
                        type: 'tabs',
                        defaultCollapsed: false,
                      },
                    }}
                  />
                  {errorMessage && (
                    <div className="mt-6 text-body-sm text-primary-red">{errorMessage}</div>
                  )}
                </div>
              )}
              <div className="mt-24 border-0 border-b border-t border-solid border-grayscale-20 py-24">
                <div className="flex items-start justify-between">
                  <div className="font-serif text-headline-xs font-bold leading-7 text-black">
                    {isUserCBPT && nextBillingDate
                      ? `You'll be charged on ${nextBillingDate}`
                      : "Today's Total"}
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="font-serif text-headline-xs font-bold leading-7 text-black">
                      ${currentPrice.toFixed(2)}
                    </span>
                  </div>
                </div>
                {originalPrice && hasDiscount && billingFrequency && (
                  <div className="text-byline-sm text-grayscale-gray mt-8 flex font-sans">
                    {billingFrequency} after the offer concludes{' '}
                    <span className="ml-auto">${originalPrice.toFixed(2)}</span>
                  </div>
                )}
              </div>
              {isReferral && (
                <div className="mt-24 text-center font-sans text-button-sm text-grayscale-30">
                  By beginning your subscription, we will be sharing your name with{' '}
                  {referringSubscriberName}, who has referred you, and letting them know that
                  you&apos;ve started enjoying The Information.
                </div>
              )}
              <Disclosure />
              <Button
                disabled={!stripe || isLoading}
                type="submit"
                variant="logo"
                className={clsx('w-full', {
                  '!bg-pro-red': isPro,
                  '!bg-black': !isPro,
                })}
              >
                {isLoading ? (
                  <CustomSpinner
                    color="#fff"
                    size={10}
                    style={{ display: 'flex', justifyContent: 'center' }}
                  />
                ) : (
                  <>{buttonText}</>
                )}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </ReCAPTCHA>
  );
};

export default PaymentMethod;
